import { __rest } from '../../../../node_modules/tslib/tslib.es6.js';
import { jsx } from 'react/jsx-runtime';
import React__default, { useState, useRef, useCallback, useImperativeHandle, startTransition } from 'react';
import SelectField from '../../FieldControlled/SelectField/SelectField.js';
import '../../FieldControlled/common.js';
import { isObjectItem } from '../../FieldControlled/SelectField/common.js';
import { ValidationRules } from '../../../Validation/types.js';
import { isUndefined, isEqualUndefinedOrEmpty } from '../../../../utils/js.js';
import useComponentStateWithValidations from '../hooks/useComponentStateWithValidations.js';
import { createFocus, createContains } from '../../FieldControlled/shared.js';
import useFieldValue from '../hooks/useFieldValue.js';
import useComboboxItems from './useComboboxItems.js';
import useFormValueChanged from '../hooks/useFormValueChanged.js';
import useFormReference from '../hooks/useFormReference.js';
import LoadingSession from './LoadingSession.js';
import { EmphasisLM } from '../../../types.js';

function useComboboxReference(value, validation, setValue, setTouched, setSearchValue, reloadItems, setFormValidation) {
    const comboRef = {
        setValue: (newValue) => {
            setValue(newValue, false);
        },
        changeValue: (newValue) => {
            setValue(newValue, false);
            setTouched(true);
        },
        initializeValue: (newValue) => {
            setValue(newValue, true);
            setTouched(false);
        },
        getValue: () => value,
        validate: () => {
            setValue(value, false);
            setTouched(true);
        },
        setSearchValue: (searchTerm) => {
            setSearchValue(searchTerm);
        },
        getValidation: () => validation,
        reloadItems,
        setFormValidation,
    };
    const ref = useRef(comboRef);
    ref.current = comboRef;
    return ref;
}
function getSearchValueByItemDefault(item) {
    if (isObjectItem(item))
        return '';
    return item;
}
const ComboboxComp = React__default.forwardRef((_a, ref) => {
    var { validations, formFieldName, isValueEqual = isEqualUndefinedOrEmpty, onSelected, onLoadItems, onAddItem, onUpdateItem, onRemoveItem, getValueByItem, getItemByValue, getSearchValueByItem = getSearchValueByItemDefault, debounceSearchTextDelay, itemKeyProperty, pageSize } = _a, selectFieldProps = __rest(_a, ["validations", "formFieldName", "isValueEqual", "onSelected", "onLoadItems", "onAddItem", "onUpdateItem", "onRemoveItem", "getValueByItem", "getItemByValue", "getSearchValueByItem", "debounceSearchTextDelay", "itemKeyProperty", "pageSize"]);
    const notifyValueChange = useFormValueChanged(formFieldName, onSelected);
    const [searchValue, setSearchValue] = useState('');
    const [state, stateMessage, stateToShow, stateMessageToShow, updateState, setTouched, setFormValidation,] = useComponentStateWithValidations(formFieldName, notifyValueChange, validations, isValueEqual);
    const { items, reloadItems, handleSearch, stateItems, messageItems, moreItems, loadMoreItems, } = useComboboxItems(onLoadItems, debounceSearchTextDelay, pageSize);
    const loadingSessionRef = useRef(null);
    const dirtyRef = useRef(false);
    const handleValueChanged = useCallback((newValue, isOnInitialization) => {
        // se seleziono un valore, ricerco per tutti.
        if (newValue) {
            loadingSessionRef.current = new LoadingSession('').onCompleted(() => {
                loadingSessionRef.current = null;
            });
            handleSearch(loadingSessionRef.current);
        }
        // assegno al search term il valore selezionato
        if (getItemByValue && !isUndefined(newValue)) {
            getItemByValue(newValue).then((result) => {
                setSearchValue(result ? getSearchValueByItem(result) : '');
            });
        }
        else if (!isUndefined(newValue))
            setSearchValue(getSearchValueByItem(newValue));
        else
            setSearchValue('');
        updateState(newValue, isOnInitialization);
    }, [getItemByValue, getSearchValueByItem, updateState, handleSearch]);
    const [value, setValue] = useFieldValue(handleValueChanged);
    const reference = useComboboxReference(value, { state, stateMessage }, setValue, setTouched, setSearchValue, reloadItems, setFormValidation);
    useFormReference(formFieldName, reference);
    const selectRef = useRef(null);
    useImperativeHandle(ref, () => (Object.assign(Object.assign({}, reference.current), { focus: createFocus(selectRef), contains: createContains(selectRef) })), [reference.current]);
    const handleSearchValueChanged = useCallback((term) => {
        dirtyRef.current = true; // dirty
        setSearchValue(term);
        startTransition(() => {
            loadingSessionRef.current = new LoadingSession(term).onCompleted(() => { loadingSessionRef.current = null; });
            handleSearch(loadingSessionRef.current);
        });
    }, [handleSearch]);
    const handleSelectBlur = useCallback(() => {
        setTouched(true);
        if (searchValue === '') {
            setValue(undefined, false);
            return;
        }
        if (!dirtyRef.current)
            return;
        dirtyRef.current = false;
        function selectItem(newItem) {
            var _a;
            (_a = selectRef.current) === null || _a === void 0 ? void 0 : _a.selectItem(newItem !== null && newItem !== void 0 ? newItem : undefined);
            setSearchValue(newItem ? getSearchValueByItem(newItem) : '');
            if (searchValue !== '' && !newItem) {
                handleSearchValueChanged('');
            }
        }
        if (loadingSessionRef.current != null) {
            loadingSessionRef.current.onLoaded((newItem) => {
                selectItem(newItem !== null && newItem !== void 0 ? newItem : undefined);
            });
        }
        else {
            selectItem(items[0]);
        }
    }, [setTouched, searchValue, setValue, getSearchValueByItem, handleSearchValueChanged, items]);
    const handleSelected = useCallback((item) => {
        dirtyRef.current = false;
        const newValue = getValueByItem ? getValueByItem(item) : item;
        setValue(newValue, false);
    }, [getValueByItem, setValue]);
    return (jsx(SelectField, Object.assign({}, selectFieldProps, { ref: selectRef, items: items, onSelectBlur: handleSelectBlur, onSelected: handleSelected, onAddItem: onAddItem && (() => onAddItem(items)), onRemoveItem: onRemoveItem && ((item) => onRemoveItem(item, items)), onUpdateItem: onUpdateItem && ((item) => onUpdateItem(item, items)), searchValue: searchValue, onSearchValueChange: handleSearchValueChanged, state: (stateItems || stateToShow), stateMessage: stateItems ? messageItems : stateMessageToShow, onShowLastItem: pageSize && moreItems && stateItems !== SelectField.States.loadingPage ? loadMoreItems : undefined, itemKeyProperty: itemKeyProperty })));
});
ComboboxComp.defaultProps = {
    onSelected: undefined,
    className: undefined,
    listClassName: undefined,
    disabled: false,
    onKeyDown: undefined,
    placeholder: undefined,
    readOnly: false,
    title: undefined,
    titleIcon: undefined,
    icon: undefined,
    inputProps: undefined,
    renderItem: undefined,
    onAddItem: undefined,
    onRemoveItem: undefined,
    onUpdateItem: undefined,
    listRight: false,
    listTop: false,
    validations: undefined,
    formFieldName: undefined,
    helperText: undefined,
    componentHeight: undefined,
    getValueByItem: undefined,
    getSearchValueByItem: undefined,
    getItemByValue: undefined,
    debounceSearchTextDelay: 300,
    itemKeyProperty: undefined,
    pageSize: undefined,
    emphasis: EmphasisLM.MEDIUM,
    tooltipProps: undefined,
    isValueEqual: isEqualUndefinedOrEmpty,
    addItemLabel: 'Aggiungi voce alla lista',
    flowMode: undefined,
};
/**
Componente che permette di scegliere un valore tra una lista di elementi.

Vedi [Design System di CGN - Campi di Input - Combobox](https://zeroheight.com/1b5e94371/p/940c42-campi-di-input/b/405dcb)

Alla scrittura delle testo verrà attivata una callback (`onLoadItems`) e comparirà il popup.
Al click di un elemento del popup il valore verrà selezionato (`onSelected`).

La lista gestisce anche le operazioni CUD.
Per inserire un elemento, aggiungere la callback `onAddItem`.
Per modificare/cancellare un elemento aggiungere la callback onUpdateItem/onRemoveItem e impostare la proprietà `enableUpdate=true` sull'elemento della lista.

E' possibile anche disabilitare la scelta di un elemento aggiungendo la proprietà `disabled=true`,

Il rendering dei componenti è svolto dalla funzione `renderItem`. Si consiglia l'utilizzo del componente `Combobox.Item`.
Nel caso di liste di oggetti, usare la proprietà `key` per definire un identificativo univoco dell'elemento.

NOTA: nel caso di un componente più semplice, con lista di dimensioni piccole e gestione sincrona,
utilizzare il componente `Select`.

Ref:
- `setValue`: funzione per cambiare il valore dell'input.
- `initializeValue`: funzione per inizializzare il valore dell'input. Cambia lo stato di touched del componente.
- `changeValue`: funzione per inizializzare il valore dell'input. Cambia lo stato di touched del componente.
- `getValue`: funzione per inizializzare il valore dell'input. Cambia lo stato di touched del componente.
- `validate`: valida il valore attualmente inserito.
- `setSearchValue`: funzione per cambiare il valore dell'input.
- `reloadItems`: ricarica tutti gli elementi con onLoadItems (se paginato reinizia dalla prima pagina).
- `validate`: valida il valore attualmente inserito.
- `focus`: funzione per tentare di acquisire il focus.
- `contains`: funzione per verificare se il componente "contiene" un altro componente nel suo sotto albero.

Constants:
- `Combobox.Validations`: validazioni fornite dal framework (vedi proprietà `validations`)
- `Combobox.States`: stati ammessi per la proprietà state;
- `Combobox.Item`: componente di default per il rendering degli elementi della lista;

*/
const Combobox = ComboboxComp;
Combobox.Validations = ValidationRules;
Combobox.Item = SelectField.Item;
Combobox.States = SelectField.States;

export { Combobox as default };
